import React from "react"
import { ShoppingCartIcon } from "@heroicons/react/24/solid"
import { TrashIcon } from "@heroicons/react/24/outline"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import useT from "../../hooks/useT"
import { cartCount } from "../../store/cart"
import { useCurrency } from "../../store/currency"

/**
 * What the cart is priced in. A basket can hold products priced in different
 * currencies and a payment has only one, so the cart picks: everything is
 * shown, and charged, in the chosen one. With nothing to choose between — one
 * currency on offer, or no rates to be had — it says nothing.
 */
function CurrencyPicker() {
  const { currency, setCurrency, currencies } = useCurrency()
  if (currencies.length < 2) return null

  const options = currencies.map(({ code }) => ({ value: code.toLowerCase(), name: code.toUpperCase() }))
  const selected = options.filter((option) => option.value === currency)

  return (
    <DropdownSelect
      options={options}
      value={selected}
      onChange={(picked) => {
        const option = Array.isArray(picked) ? picked[0] : picked
        if (option?.value) setCurrency(option.value)
      }}
      showCheckbox={false}
      showSelectionTick
      searchEnabled={false}
      enableResetButton={false}
      allowEmptySelection={false}
      menuPosition="bottom"
      dropdownWidth={110}
      // the header is the dark bar: the trigger belongs to it, the menu to
      // the site's own surface, which the variant already styles
      styleOverrides={{
        container: "relative inline-block text-left w-[86px]",
        button:
          "inline-flex items-center justify-between w-full rounded-md border border-white/25 px-2.5 h-8 bg-transparent text-[13px] font-semibold text-white hover:bg-white/10 focus:outline-none",
        selectedText: "text-white font-semibold",
        placeholderText: "text-white/70",
      }}
      className="text-white"
      data-testid="cart-currency"
    />
  )
}

/** Cart drawer title (fragment "shopping-cart-title") */
export default function CartTitle({ store }) {
  const t = useT()
  const count = cartCount(store.cart)
  return (
    <span className="flex w-full items-center gap-2">
      <ShoppingCartIcon className="w-6 h-6" />
      <span>{t`Cart`}</span>
      {count > 0 && <span className="text-[14px] font-normal opacity-80">({count})</span>}
      {/* the whole cart emptied, asked first */}
      {count > 0 && (
        <button
          type="button"
          onClick={() =>
            showPromptDialog({
              title: t`Clear the cart?`,
              message: t`All products will be removed from the cart.`,
              confirmText: t`Clear`,
              cancelText: t`Cancel`,
              onConfirm: () => store.emptyCart(),
            })
          }
          className="ml-1 p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10"
          title={t`Clear the cart`}
          aria-label={t`Clear the cart`}
          data-testid="cart-clear"
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      )}
      {/* the far side of the header, clear of the close button */}
      <span className="ml-auto mr-9 flex items-center">
        <CurrencyPicker />
      </span>
    </span>
  )
}
